import { useMemo, useState } from 'react';
import { Button, Input, Modal, SearchableSelect } from '@elka/ui';
import type { CallConnection, Contact, Deal } from '@elka/types';
import { useAuth } from '../auth/hooks/useAuth';
import { useContacts } from '../contacts/hooks/useContacts';
import { useCompanies } from '../companies/hooks/useCompanies';
import { useDeals } from '../deals/hooks/useDeals';
import { useLogActivity } from '../activities/hooks/useLogActivity';
import { FollowUpChips, OutcomeChips } from '../activities/components/Composer';
import { buildLogInput, followUpOption, loggedMessage } from '../activities/utils/quickLog';
import { FIELD_TEXTAREA } from '../board/pageStyles';
import { OPEN_STAGES } from '../../constants/stageTokens';
import { formatPhoneInput, validatePhone } from '../../lib/validation';
import { useCrmActions } from './useCrmActions';

export type QuickLogRequest =
  | { kind: 'call' | 'meeting'; contactId?: string; dealId?: string }
  | { kind: 'contact'; companyId?: string };

// @elka/ui Modal restyled to the calm dialog: 500px, radius 12, 35% ink overlay, no blur.
// The `!` beats the Modal's own classes (no tailwind-merge) and its inline max-width.
const DIALOG_CLASSES = {
  backdropClassName: '!bg-ink/35 !backdrop-blur-none',
  panelClassName:
    '!max-w-[500px] !rounded-xl !border-line !bg-surface !shadow-overlay !backdrop-blur-none',
  headerClassName:
    '!border-0 !px-5 !pt-5 !pb-0 [&_h2]:text-ink [&_h2]:text-[17px] [&_h2]:font-extrabold',
  contentClassName: '!px-5 !py-3.5',
  footerClassName: '!border-0 !bg-transparent !px-5 !pt-1 !pb-5',
} as const;

function DialogFooter({
  onSave,
  onClose,
  isSaving,
  disabled,
}: {
  onSave: () => void;
  onClose: () => void;
  isSaving: boolean;
  disabled?: boolean;
}) {
  return (
    <div className="flex gap-2">
      <Button calm size="lg" onClick={onSave} isLoading={isSaving} disabled={disabled}>
        שמור
      </Button>
      <Button calm size="lg" variant="secondary" onClick={onClose} disabled={isSaving}>
        ביטול
      </Button>
    </div>
  );
}

const isOpenDeal = (d: Deal) => OPEN_STAGES.includes(d.stage);

/** The contact's current deal if still open, else its newest open deal. */
function openDealFor(contact: Contact, deals: Deal[]): Deal | undefined {
  const current = deals.find((d) => d.id === contact.currentDealId);
  if (current && isOpenDeal(current)) return current;
  return deals.find((d) => d.contactId === contact.id && isOpenDeal(d));
}

function LogDialog({
  type,
  initialContactId,
  initialDealId,
  onClose,
}: {
  type: 'call' | 'meeting';
  initialContactId?: string;
  initialDealId?: string;
  onClose: () => void;
}) {
  const { user } = useAuth();
  const { toast } = useCrmActions();
  const { contacts, isLoading: contactsLoading } = useContacts();
  const { deals, isLoading: dealsLoading } = useDeals();
  const { logActivity, isSubmitting } = useLogActivity();
  // null until the user picks: until then the caller's contact/deal apply.
  const [pickedContactId, setPickedContactId] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<CallConnection>('answered');
  const [text, setText] = useState('');
  const [followDays, setFollowDays] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const pinnedDeal =
    pickedContactId === null && initialDealId
      ? deals.find((d) => d.id === initialDealId)
      : undefined;
  const contactId = pickedContactId ?? initialContactId ?? pinnedDeal?.contactId ?? '';
  const contact = contacts.find((c) => c.id === contactId);
  const deal = pinnedDeal ?? (contact ? openDealFor(contact, deals) : undefined);
  const companyName = contact?.companyName || deal?.companyName || '';

  const contactOptions = useMemo(
    () =>
      contacts.map((c) => ({
        value: c.id,
        label: c.companyName ? `${c.name} — ${c.companyName}` : c.name,
        subtitle: c.phone,
      })),
    [contacts]
  );

  const hint = [companyName, deal && `יירשם על העסקה ״${deal.name}״`].filter(Boolean).join(' · ');

  const handleSave = async () => {
    if (!user) return;
    if (!contact && !deal) {
      setError('בחר איש קשר');
      return;
    }
    setError(null);
    const performer = { uid: user.uid, name: user.displayName || user.email || '' };
    const target = {
      contactId: contact?.id ?? deal?.contactId,
      contactName: contact?.name ?? deal?.contactName,
      companyId: contact?.companyId || deal?.companyId,
      companyName: companyName || undefined,
      dealId: deal?.id,
      dealName: deal?.name,
    };
    try {
      await logActivity(
        buildLogInput(type, target, text, performer, type === 'call' ? outcome : undefined),
        followUpOption(followDays, performer)
      );
      toast(loggedMessage(type, followDays !== null));
      onClose();
    } catch (err) {
      console.error('Quick log failed:', err);
      setError('השמירה נכשלה, נסה שוב');
    }
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={type === 'call' ? 'רשום שיחה' : 'רשום פגישה'}
      closeOnBackdropClick={false}
      {...DIALOG_CLASSES}
      footer={
        <DialogFooter
          onSave={handleSave}
          onClose={onClose}
          isSaving={isSubmitting}
          disabled={contactsLoading || dealsLoading}
        />
      }
    >
      <div className="flex flex-col gap-3.5">
        <div>
          <SearchableSelect
            label="עם מי?"
            options={contactOptions}
            value={contactId}
            onChange={(value) => {
              setPickedContactId(value);
              setError(null);
            }}
            placeholder="חפש איש קשר..."
            emptyMessage="לא נמצאו אנשי קשר"
            clearable
          />
          {hint && <p className="text-muted mt-1 text-[11px]">{hint}</p>}
        </div>
        {type === 'call' && <OutcomeChips value={outcome} onChange={setOutcome} />}
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={3}
          placeholder="מה סוכם?"
          aria-label="מה סוכם?"
          className={`${FIELD_TEXTAREA} resize-y`}
        />
        <FollowUpChips value={followDays} onChange={setFollowDays} />
        {error && (
          <p role="alert" className="text-danger text-sm font-medium">
            {error}
          </p>
        )}
      </div>
    </Modal>
  );
}

function NewContactDialog({
  initialCompanyId,
  onClose,
}: {
  initialCompanyId?: string;
  onClose: () => void;
}) {
  const { toast } = useCrmActions();
  const { addContact } = useContacts();
  const { companies } = useCompanies();
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [position, setPosition] = useState('');
  const [companyId, setCompanyId] = useState(initialCompanyId ?? '');
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const companyOptions = useMemo(
    () => companies.map((c) => ({ value: c.id, label: c.name })),
    [companies]
  );

  const handleSave = async () => {
    const company = companies.find((c) => c.id === companyId);
    if (!name.trim()) return setError('הזן שם');
    if (!company) return setError('בחר לקוח');
    if (phone && !validatePhone(phone)) return setError('פורמט טלפון לא תקין');
    setError(null);
    setIsSaving(true);
    try {
      await addContact({
        name: name.trim(),
        position: position.trim(),
        companyId: company.id,
        companyName: company.name,
        ...(phone ? { phone } : {}),
      });
      toast('איש הקשר נוסף');
      onClose();
    } catch (err) {
      console.error('Add contact failed:', err);
      setError('השמירה נכשלה, נסה שוב');
      setIsSaving(false);
    }
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      title="איש קשר חדש"
      closeOnBackdropClick={false}
      {...DIALOG_CLASSES}
      footer={<DialogFooter onSave={handleSave} onClose={onClose} isSaving={isSaving} />}
    >
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Input
          label="שם"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          autoFocus
        />
        <Input
          label="טלפון"
          type="tel"
          dir="ltr"
          className="text-right"
          value={phone}
          onChange={(e) => setPhone(formatPhoneInput(e.target.value))}
        />
        <Input label="תפקיד" value={position} onChange={(e) => setPosition(e.target.value)} />
        <SearchableSelect
          label="לקוח"
          options={companyOptions}
          value={companyId}
          onChange={setCompanyId}
          placeholder="חפש לקוח..."
          emptyMessage="לא נמצאו לקוחות"
          required
        />
        {error && (
          <p role="alert" className="text-danger text-sm font-medium sm:col-span-2">
            {error}
          </p>
        )}
      </div>
    </Modal>
  );
}

/** The global pill's dialogs (README §8): quick call / meeting log, or a new contact. */
export function QuickLogModal({
  request,
  onClose,
}: {
  request: QuickLogRequest;
  onClose: () => void;
}) {
  return request.kind === 'contact' ? (
    <NewContactDialog initialCompanyId={request.companyId} onClose={onClose} />
  ) : (
    <LogDialog
      type={request.kind}
      initialContactId={request.contactId}
      initialDealId={request.dealId}
      onClose={onClose}
    />
  );
}
